/**
 * @file: One snapshot of the intervals that the grid shows, with the results
 * that more than one reader needs.
 *
 * The strip, the ribbon, the marks on the bars and the panel all read the
 * same intervals. Each reader read the events of FullCalendar again, and the
 * strip and the ribbon each cut the same days into the same pieces. The ribbon
 * did that on each frame of a scroll. This module makes the list and the
 * results one time for each change of the events or of the dates.
 *
 * FullCalendar calls eventClassNames, eventContent and eventDidMount during
 * its render. It sends eventsSet and datesSet after that render. A snapshot
 * that changes at eventsSet is therefore old in the render that moves a bar.
 * FullCalendar replaces its event store and its date profile before each
 * render, so the keeper compares those two objects at each read.
 */

import moment from 'moment'
import { neverOnScreen, segmentsForRange } from '../../../shared/on-screen-segments'
import { intervalsOf } from './calendar-intervals'

/** The format of the data-date attribute of a day column. */
const DATE_KEY = 'YYYY-MM-DD'

/**
 * Give the pieces of one day, with their winners.
 *
 * An unreadable date gives no pieces. The strip and the ribbon then draw
 * nothing for that column.
 *
 * @param {Array} intervals - From intervalsOf
 * @param {string} date - 'YYYY-MM-DD' of a day column
 * @returns {Array} The result of segmentsForRange for that day
 */
function segmentsOfDay (intervals, date) {
  const dayStart = new Date(`${date}T00:00:00`)

  if (!isFinite(dayStart.getTime())) {
    return []
  }

  const dayEnd = new Date(dayStart.getTime())
  dayEnd.setDate(dayEnd.getDate() + 1)

  return segmentsForRange(intervals, dayStart, dayEnd)
}

/** Give the dates of the days in the range, as 'YYYY-MM-DD'. */
function datesIn (range) {
  const first = moment(range.start).startOf('day')
  const count = Math.max(0, Math.round(moment(range.end).diff(first, 'days', true)))

  return Array.from({ length: count }, (_, index) => first.clone().add(index, 'days').format(DATE_KEY))
}

/**
 * Give the ids of the intervals that never reach the screen in the range.
 *
 * An interval that continues past an edge of the range is not included. The
 * range alone cannot show whether that interval reaches the screen outside
 * the range.
 *
 * @param {Array} intervals - From intervalsOf
 * @param {Object|null} range - { start, end }. No range gives no ids
 * @returns {Set} The interval ids
 */
export function neverOnScreenIdsOf (intervals, range) {
  if (!range) {
    return new Set()
  }

  return new Set(neverOnScreen(intervals, range.start, range.end)
    .filter(entry => !entry.clipped)
    .map(entry => entry.interval.id))
}

/**
 * Make the snapshot of a list of intervals and a visible range.
 *
 * The pieces of each day in the range are made at once. segmentsOn makes the
 * pieces of a day outside the range at the first read. Do not change
 * the set of never-on-screen ids. Other readers hold the same set.
 *
 * @param {Array} intervals - From intervalsOf
 * @param {Object|null} range - { start, end } of the view. Null when there is no view
 * @returns {Object} A frozen { intervals, range, neverOnScreenIds,
 *   segmentsOn(date) }. The range is the argument
 */
export function weekFrom (intervals, range) {
  const shown = Object.freeze(intervals.map(interval => Object.freeze(Object.assign({}, interval))))
  const piecesOf = date => Object.freeze(segmentsOfDay(shown, date).map(piece => Object.freeze(piece)))
  const byDate = new Map(range ? datesIn(range).map(date => [date, piecesOf(date)]) : [])

  return Object.freeze({
    intervals: shown,
    range: range ? Object.freeze(Object.assign({}, range)) : null,
    neverOnScreenIds: neverOnScreenIdsOf(shown, range),
    segmentsOn: date => byDate.get(date) || piecesOf(date)
  })
}

/** The snapshot when there is no calendar. */
export const EMPTY_WEEK = weekFrom([], null)

/**
 * Make the snapshot of the events and the dates that a calendar holds now.
 *
 * @param {Object|null} calendar - A FullCalendar instance
 * @returns {Object} See weekFrom
 */
export function weekSnapshotOf (calendar) {
  const view = calendar && calendar.view

  return weekFrom(intervalsOf(calendar), view ? { start: view.activeStart, end: view.activeEnd } : null)
}

/**
 * Give the objects that FullCalendar replaces at each change of its events or
 * of its dates, or null.
 *
 * getEvents reads the same event store (core/internal-common.js, getEvents),
 * and the view reads the same date profile. Each object is new after a fetch,
 * a drop, a resize, a revert and a move to another week.
 *
 * getCurrentData is not in the documented API of FullCalendar. Without it the
 * keeper makes a new snapshot at each read. That is slower, but never old.
 */
function sourceOf (calendar) {
  if (typeof calendar.getCurrentData !== 'function') {
    return null
  }

  const data = calendar.getCurrentData()

  return { eventStore: data.eventStore, dateProfile: data.dateProfile }
}

const sameSource = (one, other) => Boolean(one && other) &&
  one.eventStore === other.eventStore && one.dateProfile === other.dateProfile

/**
 * Give a function that returns the snapshot of a calendar. The function makes
 * a new snapshot only when FullCalendar has new events or new dates.
 *
 * @returns {Function} (calendar) => snapshot. No calendar gives EMPTY_WEEK
 */
export function weekKeeper () {
  let kept = { source: null, week: EMPTY_WEEK }

  return calendar => {
    if (!calendar) {
      return EMPTY_WEEK
    }

    const source = sourceOf(calendar)

    if (!sameSource(source, kept.source)) {
      kept = { source, week: weekSnapshotOf(calendar) }
    }

    return kept.week
  }
}
